import React, {useMemo, useState, useEffect} from 'react';
import {Divider} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import {t} from '../../../../../../services/i18n';
import {Colors} from '../../../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../../../styles/GlobalStyles';
import VStack from '../../../../../common/Layout/VStack';
import AppText from '../../../../../common/AppText';
import CustomCollapse from '../../../../../common/CustomCollapse';
import HugCollapseLabel from './HugCollapseLabel';
import HugCollapseBody from './HugCollapseBody';
import {fetchApi} from '../../../../../../services/HTTP';
import usePermissions from '../../../../../../services/Permissions';
import {sortString, dbDateFormat} from '../../../../../../services/Helpers';
import ChooseHugToSwapSkeleton from '../Steps/ChooseHugToSwapSkeleton';
import FiltersHandler from '../../../../../common/FiltersHandler';
import {getHugFiltersConfig, ALL_LOCATIONS_KEY} from './hugFiltersConfig';
import {updateTempSelection} from '../../../../../../redux/stepperSlice';

const ChooseHug = (props) => {
    const {setFieldValue, target, values, errors} = props;

    const dispatch = useDispatch();
    const locationsBox = useSelector((state) => state.box.box?.locations_box);
    const selectedLocation = useSelector((state) => state.userSettings.scheduleLocation);
    const { has } = usePermissions();

    const isMultiLocation = useMemo(() => (locationsBox?.length ?? 0) > 1, [locationsBox]);
    const showCategoryGroupFilter = useMemo(() => has('boxCategoriesGroups') && !has('boxCategoriesGroupsLock'), []);
    const canceledCourseLocation = target?.membershipUser?.location ?? selectedLocation
    const [isLoading, setIsLoading] = useState(false);
    const [selectedHugId, setSelectedHugId] = useState(() =>
        values?.eventData?.series?.id != null ? String(values.eventData.series.id) : null
    );
    const [expandedHugId, setExpandedHugId] = useState(null);
    const [hugimList, setHugimList] = useState([]);
    const [filteredHugim, setFilteredHugim] = useState([]);
    const [showLocation, setShowLocation] = useState(true);

    useEffect(() => {
        if (values?.eventData?.series?.id && target?.user?.id) {
            dispatch(updateTempSelection({series_id: values.eventData.series.id, user_fk: target.user.id}));
        } else {
            dispatch(updateTempSelection(null));
        }
    }, [values?.eventData?.series?.id, target?.user?.id]);

    useEffect(() => {
        fetchHugimForSwap();
    }, []);

    const fetchHugimForSwap = async () => {
        try {
            setIsLoading(true);
            const res = await fetchApi('hugim/getHugimForSwap', 'POST', {user_id: target.user.id}, false, true);
            setHugimList(res);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
        }
    };

const getHugRibbon = (hug) => {
    if (hug.isBookedUser) return { text: t('already-booked'), color: '#E0E0E0', isDisabled: true };
    if (hug.isHugFull) return { text: t('hug-is-full'), color: '#E0E0E0', isDisabled: true};
    if (hug.remainingSlots != null) return { text: t('spots-remaining', { count: hug.max_users - hug.series_membership_user.length }), color: Colors.newDesign.lightGreen };
    return undefined;
};

    const locationOptions = useMemo(() => {
        if (!isMultiLocation) return [];
        return [
            { value: ALL_LOCATIONS_KEY, label: t('all-locations') },
            ..._.sortBy(locationsBox ?? [], l => sortString(l.location)).map(l => ({ value: l.id, label: l.location })),
        ];
    }, [isMultiLocation, locationsBox]);

    const filtersConfig = useMemo(
        () => getHugFiltersConfig({ showCategoryGroupFilter, isMultiLocation, locationOptions, canceledCourseLocation }),
        [showCategoryGroupFilter, isMultiLocation, locationOptions, canceledCourseLocation],
    );

    const groupedHugim = useMemo(() => {
        const grouped = _.groupBy(filteredHugim, hug => hug.box_categories?.name);
        const sortedKeys = _.sortBy(Object.keys(grouped), k => sortString(k));
        return _.fromPairs(sortedKeys.map(k => [k, grouped[k]]));
    }, [filteredHugim]);

    const getHugMembershipStartDate = (series) => {
        const today = moment().format(dbDateFormat);
        const seriesStart = series?.start_date;
        if (!seriesStart) {
            return today;
        }
        return moment(seriesStart, dbDateFormat).isAfter(moment(), 'day') ? seriesStart : today;
    };

    useEffect(() => {
        if (selectedHugId !== null || values?.eventData?.series?.id) {
            return;
        }
        const allHugs = Object.values(groupedHugim).flat();
        const firstAvailable = allHugs.find(hug => !hug.isBookedUser);
        if (firstAvailable) {
            const firstId = String(firstAvailable.id);
            setSelectedHugId(firstId);
            setFieldValue('eventData', { series: firstAvailable, date: getHugMembershipStartDate(firstAvailable) });
        }
    }, [groupedHugim, selectedHugId, values?.eventData?.series?.id]);

    const onExpand = (hugId) => setExpandedHugId(prev => prev === hugId ? null : hugId);

    const onSelect = (hugId) => {
        const newId = selectedHugId === hugId ? null : hugId;
        const hug = hugimList.find(h => h.id === Number(hugId));
        setSelectedHugId(newId);
        setFieldValue('eventData', newId && hug ? { series: hug, date: getHugMembershipStartDate(hug) } : null);
    };

    const onFiltered = (filtered, filterValues) => {
        setFilteredHugim(filtered);
        setShowLocation(!filterValues.location || filterValues.location === ALL_LOCATIONS_KEY);
    };

    const buildHugCollapseItem = (hug) => ({
        key: String(hug.id),
        label: <HugCollapseLabel hug={hug} showLocation={showLocation} />,
        children: <HugCollapseBody hug={hug} />,
        ribbon: getHugRibbon(hug),
    });

    if (isLoading) return <ChooseHugToSwapSkeleton />;

    return (
        <VStack space={'0.75rem'}>
            <FiltersHandler
                list={hugimList}
                config={filtersConfig}
                onFiltered={onFiltered}
            />
            <VStack space={'1.25rem'}>
                <Divider style={{ margin: 0 }} />
                {Object.entries(groupedHugim).map(([categoryName, hugimInCategory]) => (
                    <VStack key={categoryName} space={'1.25rem'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading2Desktop} boldFont>
                            {categoryName}
                        </AppText>
                        <VStack space={'0'}>
                            <Divider style={{ margin: 0 }} />
                            {hugimInCategory.map((hug, idx) => (
                                <React.Fragment key={hug.id}>
                                    {idx > 0 && <Divider style={{ margin: 0 }} />}
                                    <CustomCollapse
                                        item={buildHugCollapseItem(hug)}
                                        isExpanded={expandedHugId === String(hug.id)}
                                        isSelected={selectedHugId === String(hug.id)}
                                        onExpand={onExpand}
                                        onSelect={onSelect}
                                        isBookedUser={!!hug.isBookedUser}
                                    />
                                </React.Fragment>
                            ))}
                            <Divider style={{ margin: 0 }} />
                        </VStack>
                    </VStack>
                ))}
                {Object.keys(groupedHugim).length === 0 && (
                    <AppText fontSize={GlobalStyleAttributes.fonts.standard16} style={{ padding: '2rem 0' }}>
                        {t('no-hugim-found')}
                    </AppText>
                )}
            </VStack>
        </VStack>
    );
};

export default ChooseHug;
